import { Box, IconButton, Typography } from '@mui/material';
import { MunicipalTabulation, ProvincialDistrictTabulation, Tabulation, User } from '../../types';
import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';

import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TabulationDash from './TabulationDash';
import { db } from '../../firebase';
import useAuth from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

const Dashboard: React.FC = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [tabulations, setTabulations] = useState<Tabulation[]>([]);
  const [currentTabIndex, setCurrentTabIndex] = useState(0);

  // Redirect if no view_dashboard permission
  useEffect(() => {
    if (!loading && (!user || !user.permissions.view_dashboard)) {
      navigate('/polls');
    }
  }, [user, loading, navigate]);

  const getTabulations = async (user: User) => {
    const districtsCol = collection(db, `tabulationsV2/${user.dashboard}/regions/REGION XIII/provinces/SURIGAO DEL NORTE/districts`);
    const districsSnap = await getDocs(districtsCol);
    const districtsTabs = districsSnap.docs.map((d) => d.data() as ProvincialDistrictTabulation);

    const cityTabs: Tabulation[] = [];
    for (const districtTab of districtsTabs) {
      const citySnap = await getDocs(collection(districtsCol, `${districtTab.district}/cities`));
      cityTabs.push(...citySnap.docs.map((d) => d.data() as MunicipalTabulation));
    }

    const allTabulations = [
      ...districtsTabs,
      ...cityTabs
    ];

    if (user.allowedTabulations) {
      const filteredTabulations = allTabulations.filter((tabulation) => {
        for (const allowedTabulation of user.allowedTabulations!) {
          if (tabulation.id === allowedTabulation.id && tabulation.type === allowedTabulation.type) {
            return true;
          }
        }
        return false;
      });
      setTabulations(filteredTabulations);
    } else {
      setTabulations(allTabulations);
    }
  };

  useEffect(() => {
    if (user) {
      getTabulations(user);
    }
  }, [user]);

  const navigateTab = (direction: 'next' | 'prev') => {
    const totalTabs = tabulations.length;
    setCurrentTabIndex((prevIndex) => {
      if (direction === 'next') {
        return (prevIndex + 1) % totalTabs;
      } else {
        return (prevIndex - 1 + totalTabs) % totalTabs;
      }
    });
  };
  
  // If loading, return null to prevent flash of content
  if (loading) {
    return null;
  }
  
  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">
          Dashboard
        </Typography>
      </Box>
      <Box position="relative" height="100%" overflow="hidden">
        {tabulations.length > 0 && (
          <Box 
            sx={{
              transition: 'opacity 0.5s ease-in-out',
              opacity: 1,
              position: 'relative',
              display: 'flex',
              alignItems: 'start'
            }}
          >
            {tabulations.length > 1 && (
              <IconButton 
                onClick={() => navigateTab('prev')} 
                sx={{ 
                  position: 'absolute', 
                  left: 0, 
                  zIndex: 10 
                }}
              >
                <ArrowBackIosIcon />
              </IconButton>
            )}

            <TabulationDash tabulation={tabulations[currentTabIndex]} navToStats />
            
            {tabulations.length > 1 && (
              <IconButton 
                onClick={() => navigateTab('next')} 
                sx={{ 
                  position: 'absolute', 
                  right: 0, 
                  zIndex: 10 
                }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            )}
          </Box>
        )}
        {tabulations.length > 1 && (
          <Box 
            display="flex" 
            justifyContent="center" 
            mt={2}
          >
            {tabulations.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: index === currentTabIndex ? 'primary.main' : 'grey.300',
                  margin: '0 5px',
                  cursor: 'pointer'
                }}
                onClick={() => setCurrentTabIndex(index)}
              />
            ))}
          </Box>
        )}
      </Box>
    </>
  );
};

export default Dashboard;
